import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const menuItems = [
    {
      id: 1,
      name: 'Classic Malai Kulfi',
      description: 'Traditional creamy kulfi made with reduced milk and cardamom',
      price: '₹60',
      image: 'https://images.unsplash.com/photo-1567206563064-6f60f40a2b57?w=400&h=300&fit=crop',
      bestseller: true,
      badge: 'Classic'
    },
    {
      id: 2,
      name: 'Mango Delight',
      description: 'Fresh Alphonso mango kulfi with real mango chunks',
      price: '₹80',
      image: 'https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=400&h=300&fit=crop',
      bestseller: true,
      badge: 'Seasonal'
    },
    {
      id: 3,
      name: 'Chocolate Fudge Shake',
      description: 'Rich Belgian chocolate shake topped with whipped cream',
      price: '₹120',
      image: 'https://images.unsplash.com/photo-1572490122747-3968b75cc699?w=400&h=300&fit=crop',
      bestseller: false,
      badge: 'Premium'
    },
    {
      id: 4,
      name: 'Pista Badam Kulfi',
      description: 'Loaded with pistachios and almonds in every bite',
      price: '₹90',
      image: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=400&h=300&fit=crop',
      bestseller: true,
      badge: 'Special'
    },
    {
      id: 5,
      name: 'Strawberry Dream',
      description: 'Fresh strawberry ice cream with berry swirls',
      price: '₹70',
      image: 'https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?w=400&h=300&fit=crop',
      bestseller: false,
      badge: 'Fresh'
    },
    {
      id: 6,
      name: 'Royal Rabdi Faluda',
      description: 'Traditional faluda with rabdi, vermicelli and rose syrup',
      price: '₹150',
      image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=400&h=300&fit=crop',
      bestseller: true,
      badge: 'Royal'
    }
  ];

  const features = [
    { icon: '🥛', title: 'Fresh Ingredients', description: 'Made daily with farm-fresh milk and natural flavors' },
    { icon: '👨‍🍳', title: 'Traditional Recipes', description: 'Age-old family recipes passed through generations' },
    { icon: '❄️', title: 'Perfect Chill', description: 'Served at the perfect temperature for maximum taste' },
    { icon: '🌿', title: 'No Preservatives', description: '100% natural with zero artificial additives' }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      location: 'Delhi',
      rating: 5,
      text: 'The best kulfi I have ever tasted! It reminds me of my grandmother\'s homemade kulfi. Absolutely divine!',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Rahul Verma',
      location: 'Gurgaon',
      rating: 5,
      text: 'Amazing variety of flavors and the quality is consistently excellent. My family\'s favorite dessert spot!',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Anita Kapoor',
      location: 'Noida',
      rating: 5,
      text: 'The Mango Kulfi is out of this world! You can taste the real Alphonso mangoes. Highly recommended!',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
    }
  ];

  const galleryImages = [
    { src: 'https://images.unsplash.com/photo-1570197788417-0e82375c9371?w=600&h=400&fit=crop', title: 'Kulfi Collection' },
    { src: 'https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=400&h=400&fit=crop', title: 'Mango Special' },
    { src: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=400&h=400&fit=crop', title: 'Ice Cream Cones' },
    { src: 'https://images.unsplash.com/photo-1497034825429-c343d7c6a68f?w=400&h=400&fit=crop', title: 'Berry Delights' },
    { src: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=600&h=400&fit=crop', title: 'Dessert Paradise' },
    { src: 'https://images.unsplash.com/photo-1572490122747-3968b75cc699?w=400&h=400&fit=crop', title: 'Chocolate Heaven' }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="floating-icons">
          <span className="floating-icon">🍦</span>
          <span className="floating-icon">🍨</span>
          <span className="floating-icon">🥤</span>
          <span className="floating-icon">🍧</span>
          <span className="floating-icon">🍡</span>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">Premium Ice Cream & Kulfi</div>
            <h1>
              Experience the Magic of
              <span>Kitto Di Kulfi</span>
            </h1>
            <p>
              Discover the authentic taste of traditional Indian kulfi and artisan ice creams. 
              Made fresh daily with love, using only the finest ingredients.
            </p>
            <div className="hero-buttons">
              <Link to="/menu" className="btn btn-primary">
                Explore Menu 🍨
              </Link>
              <Link to="/contact" className="btn btn-secondary">
                Visit Us
              </Link>
            </div>
          </div>
          
          <div className="hero-image">
            <img src="/logo.jpeg" alt="Kitto Di Kulfi" />
          </div>
        </div>
      </section>

      {/* Featured Menu Section */}
      <section className="section menu-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2 className="gradient-text">Our Delicious Menu</h2>
            <p>Handcrafted with passion, served with love. Discover our most popular treats!</p>
          </div>
          
          <div className="menu-grid">
            {menuItems.map((item, index) => (
              <div 
                key={item.id} 
                className="menu-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {item.bestseller && <span className="bestseller-badge">⭐ Bestseller</span>}
                <img src={item.image} alt={item.name} className="menu-card-image" />
                <div className="menu-card-content">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                  <div className="menu-card-footer">
                    <span className="menu-price">{item.price}</span>
                    <span className="menu-badge">{item.badge}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '50px' }} className="fade-in">
            <Link to="/menu" className="btn btn-primary">
              View Full Menu →
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section features-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2>Why Choose Us?</h2>
            <p>We're committed to delivering the best ice cream experience</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <span className="feature-icon">{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section gallery-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2 className="gradient-text">Sweet Gallery</h2>
            <p>A visual treat of our delicious creations</p>
          </div>
          
          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <div key={index} className="gallery-item fade-in">
                <img src={image.src} alt={image.title} />
                <div className="gallery-overlay">
                  <span>{image.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2 className="gradient-text">What Our Customers Say</h2>
            <p>Don't just take our word for it - hear from our happy customers!</p>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="testimonial-card fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="testimonial-content">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i}>⭐</span>
                    ))}
                  </div>
                  <p className="testimonial-text">{testimonial.text}</p>
                  <div className="testimonial-author">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name} 
                      className="testimonial-avatar" 
                    />
                    <div className="testimonial-info">
                      <h4>{testimonial.name}</h4>
                      <p>{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section features-section" style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '20px' }}>
              Ready to Taste the Difference?
            </h2>
            <p style={{ fontSize: '1.2rem', opacity: 0.9, marginBottom: '35px', maxWidth: '600px', margin: '0 auto 35px' }}>
              Visit our parlour today and treat yourself to the finest kulfi and ice cream in town!
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn" style={{ 
                background: 'white', 
                color: 'var(--pink-primary)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
              }}>
                📍 Find Our Location
              </Link>
              <a href="tel:+919999999999" className="btn btn-secondary" style={{ 
                borderColor: 'white', 
                color: 'white' 
              }}>
                📞 Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
